import { Bell, Palette, Shield, User, Users } from 'lucide-react';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InviteUserModal from './InviteUserModal';

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'account';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    marketing: false,
    security: true
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([
    { id: 1, name: 'Tanmay Sharma', email: 'tanmay@coralai.com', role: 'Admin', status: 'Active' },
    { id: 2, name: 'Sarah Johnson', email: 'sarah@coralai.com', role: 'Sales Manager', status: 'Active' },
    { id: 3, name: 'Mike Chen', email: 'mike@coralai.com', role: 'Sales Rep', status: 'Inactive' },
    { id: 4, name: 'Emily Davis', email: 'emily@coralai.com', role: 'Marketing', status: 'Active' },
  ]);

  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Handle inviting new user
  const handleInviteUser = (userData: { name: string; email: string; role: string }) => {
    const newUser = {
      id: Date.now(), // Simple ID generation
      name: userData.name,
      email: userData.email,
      role: userData.role,
      status: 'Pending'
    };
    setTeamMembers(prev => [...prev, newUser]);
  };

  const tabs = [
    { id: 'account', label: 'General', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'users', label: 'Account Defaults', icon: Users },
    { id: 'security', label: 'Privacy & Consent', icon: Shield },
    { id: 'appearance', label: 'Security', icon: Palette },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">Profile Information</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      defaultValue="Tanmay"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      defaultValue="Sharma"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    defaultValue="tanmay@coralai.com"
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    defaultValue="+1 (555) 123-4567"
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">Company Information</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      defaultValue="Coral AI"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title
                    </label>
                    <input
                      type="text"
                      defaultValue="Sales Director"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <button className="bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors">
                Save Changes
              </button>
            </div>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members</h3>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <ul className="divide-y divide-gray-200">
                  {teamMembers.map((user) => (
                    <li key={user.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {user.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-500">{user.role}</span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.status === 'Active' 
                              ? 'bg-green-100 text-green-800'
                              : user.status === 'Pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status}
                          </span>
                          <button className="text-blue-600 hover:text-blue-900 text-sm font-medium">
                            Edit
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="pt-6 border-t border-gray-200">
              <button 
                onClick={() => setIsInviteModalOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Invite New User
              </button>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Authentication</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Two-Factor Authentication</h4>
                    <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    {twoFactorEnabled && (
                      <div className="flex items-center space-x-1 text-green-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium">Enabled</span>
                      </div>
                    )}
                    <button 
                      onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        twoFactorEnabled 
                          ? 'bg-red-600 text-white hover:bg-red-700' 
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {twoFactorEnabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Privacy Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Profile Visibility</h4>
                    <p className="text-sm text-gray-500">Control who can see your profile information</p>
                  </div>
                  <select className="mt-1 block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                    <option>Everyone</option>
                    <option>Team Only</option>
                    <option>Private</option>
                  </select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Activity Status</h4>
                    <p className="text-sm text-gray-500">Show when you're active or away</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Theme</h3>
              <div className="space-y-4">
                {[
                  { id: 'light', label: 'Light', description: 'A clean and bright interface' },
                  { id: 'dark', label: 'Dark', description: 'A dark theme that\'s easy on the eyes' },
                  { id: 'system', label: 'System', description: 'Automatically match your system theme' }
                ].map((themeOption) => (
                  <div key={themeOption.id} className="flex items-center space-x-3">
                    <input
                      id={themeOption.id}
                      name="theme"
                      type="radio"
                      checked={theme === themeOption.id}
                      onChange={() => setTheme(themeOption.id as 'light' | 'dark' | 'system')}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                    />
                    <label htmlFor={themeOption.id} className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{themeOption.label}</div>
                      <div className="text-sm text-gray-500">{themeOption.description}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Display Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Compact Mode</h4>
                    <p className="text-sm text-gray-500">Show more content in less space</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Show Animations</h4>
                    <p className="text-sm text-gray-500">Enable smooth transitions and animations</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors">
                Save Preferences
              </button>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <div className="space-y-6">
                {[
                  { key: 'email', label: 'Email', description: 'Sent to your email address.' },
                  { key: 'marketing', label: 'Bell', description: 'Show up in the bell icon in the HubSpot CRM navigation bar. Click on the bell to see your most recent notifications.' },
                  { key: 'push', label: 'Browser', description: 'Appear in your screen when you\'re not active in HubSpot CRM but the site is open in a browser tab.' },
                  { key: 'security', label: 'Pop-up', description: 'Appear on your screen for a few seconds when you\'re active in HubSpot CRM. They\'ll play a sound based on your preferences.' },
                ].map((notif) => (
                  <div key={notif.key} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notifications[notif.key as keyof typeof notifications]}
                          onChange={(e) => setNotifications(prev => ({
                            ...prev,
                            [notif.key]: e.target.checked
                          }))}
                        />
                        <div className={`w-11 h-6 rounded transition-colors relative ${
                          notifications[notif.key as keyof typeof notifications]
                            ? 'bg-teal-500' 
                            : 'bg-gray-300'
                        } peer-focus:ring-2 peer-focus:ring-teal-300`}>
                          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded shadow-sm transition-transform duration-200 ease-in-out flex items-center justify-center ${
                            notifications[notif.key as keyof typeof notifications]
                              ? 'translate-x-5'
                              : 'translate-x-0.5'
                          }`}>
                            {notifications[notif.key as keyof typeof notifications] && (
                              <svg className="w-3 h-3 text-teal-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{notif.label}</h4>
                      <p className="text-sm text-gray-600 mt-1">{notif.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors">
                Save Notification Settings
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 min-h-screen">
          {/* Settings Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-lg font-bold text-gray-900">Settings</h1>
          </div>

          {/* Your Preferences Section */}
          <div className="px-4 py-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">
              Your Preferences
            </h3>
            <nav className="space-y-1">
              {tabs.slice(0, 2).map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    activeTab === id
                      ? 'text-blue-600 bg-blue-50 rounded-md font-medium'
                      : 'text-gray-600 hover:text-gray-900 font-normal'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {/* Account Management Section */}
          <div className="px-4 py-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">
              Account Management
            </h3>
            <nav className="space-y-1">
              {tabs.slice(2).map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    activeTab === id
                      ? 'text-blue-600 bg-blue-50 rounded-md font-medium'
                      : 'text-gray-600 hover:text-gray-900 font-normal'
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-white">
          {/* Content Header */}
          <div className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900">
              {tabs.find(tab => tab.id === activeTab)?.label}
            </h2>
          </div>

          {/* Content Body */}
          <div className="px-8 py-6 max-w-4xl">
            {renderTabContent()}
          </div>
        </div>
      </div>
      
      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteUser}
      />
    </div>
  );
};

export default Settings;
